/**
 * The text editor. Notepad, plus the few things a CAD program needs from one.
 *
 * A real `<textarea>` does the editing — caret, selection, clipboard, native
 * undo, IME — with a coloured `<pre>` layered exactly behind it. Nothing here
 * reimplements text entry, which is why it behaves like the box the operating
 * system gives you and not like a half-finished word processor.
 */

import {
  LANGUAGES, detectLanguage, languageById, tokenize, HIGHLIGHT_LIMIT,
} from '@sircad/core/text/highlight.ts';
import {
  columnCount, columnName, parseTable, writeTable,
} from '@sircad/core/text/csv.ts';

export interface TextFile {
  text: string;
  /** File name, for the language guess and the title. */
  fileName: string;
  /** Force a language instead of guessing one. */
  lang?: string;
  /** A line of context under the file, e.g. what generated it. */
  note?: string;
}

/** Where the file was left: caret and scroll, so a tab reopens where it was. */
export interface ViewState {
  start: number;
  end: number;
  scrollTop: number;
  scrollLeft: number;
}

export interface TextEditorOptions {
  onChange?(text: string): void;
  /** The reader chose a language by hand, overriding the guess. */
  onLanguage?(id: string): void;
  onSave?(): void;
  onSaveAs?(): void;
}

export interface TextEditor {
  el: HTMLElement;
  /** The text as edited, with the line endings the file arrived with. */
  getText(): string;
  /** Show a file, replacing whatever was in the editor. */
  load(file: TextFile): void;
  /** The language in force, whether guessed or chosen. */
  language(): string;
  focus(): void;
  getViewState(): ViewState;
  setViewState(v: ViewState): void;
  /** Recompute the colouring — after a theme change, say. */
  refresh(): void;
  /**
   * Open the find bar, on the replace field when asked.
   *
   * The Edit menu drives the same bar the toolbar and Ctrl+F do, rather than a
   * second search of its own: two ways of searching one file would each have
   * their own idea of where the last match was.
   */
  find(replace?: boolean): void;
  /** Step to the next match, or the previous one. */
  findNext(back?: boolean): void;
  /** Replace the match in hand and go to the next. */
  replaceNext(): void;
  /** Replace every match at once. */
  replaceEvery(): void;
}

/** Past this, colouring is skipped and typing stays quick. */
const BIG = 60_000;

/**
 * Ctrl keys the editor must never claim.
 *
 * A `<textarea>` already does select-all, cut, copy, paste, undo and redo
 * properly — with the real clipboard and the real undo stack, which no
 * reimplementation of them here would have.
 */
const NATIVE_KEYS = new Set(['a', 'x', 'c', 'v', 'z', 'y']);

export function createTextEditor(opts: TextEditorOptions = {}): TextEditor {
  let langId = 'plain';
  let fileName = 'untitled.txt';
  let eol: '\n' | '\r\n' = '\n';
  let loading = false;

  const root = document.createElement('div');
  root.className = 'txed';

  // --- Toolbar --------------------------------------------------------------

  const bar = document.createElement('header');
  bar.className = 'txed-bar';

  const title = document.createElement('span');
  title.className = 'txed-name';

  const note = document.createElement('span');
  note.className = 'txed-note';

  const langSel = document.createElement('select');
  langSel.className = 'txed-lang';
  langSel.title = 'Syntax colouring';
  for (const l of LANGUAGES) {
    const o = document.createElement('option');
    o.value = l.id;
    o.textContent = l.name;
    langSel.append(o);
  }
  langSel.addEventListener('change', () => {
    langId = langSel.value;
    gridBtn.hidden = !canGrid();
    if (!canGrid()) setGrid(false);
    paint();
    if (!loading) opts.onLanguage?.(langId);
    ta.focus();
  });

  const findBtn = toolButton('Find', 'Find and replace (Ctrl+F)', () => toggleFind(true));
  const wrapBtn = toolButton('Wrap', 'Wrap long lines', () => setWrap(!wrapped));
  // Offered whenever the content is a table, whatever the file is called.
  const gridBtn = toolButton('Columns', 'Show the file as a table', () => setGrid(!gridded));
  const saveBtn = toolButton('Save…', 'Write this file out (Ctrl+S)', () => opts.onSave?.());
  saveBtn.classList.add('primary');

  bar.append(title, note, langSel, gridBtn, findBtn, wrapBtn, saveBtn);

  // --- Find bar -------------------------------------------------------------

  const findRow = document.createElement('div');
  findRow.className = 'txed-find';
  findRow.hidden = true;

  const findIn = document.createElement('input');
  findIn.type = 'search';
  findIn.placeholder = 'Find';
  findIn.spellcheck = false;

  const replIn = document.createElement('input');
  replIn.type = 'search';
  replIn.placeholder = 'Replace with';
  replIn.spellcheck = false;

  const caseBtn = toolButton('Aa', 'Match case', () => {
    matchCase = !matchCase;
    caseBtn.classList.toggle('on', matchCase);
    countMatches();
  });

  const count = document.createElement('span');
  count.className = 'txed-count';

  findRow.append(
    findIn,
    toolButton('◀', 'Previous (Shift+Enter)', () => step(-1)),
    toolButton('▶', 'Next (Enter)', () => step(1)),
    caseBtn,
    count,
    replIn,
    toolButton('Replace', 'Replace this one', () => (gridded ? replaceCell() : replaceOne())),
    toolButton('All', 'Replace every one', () => (gridded ? replaceAllCells() : replaceAll())),
    toolButton('✕', 'Close (Esc)', () => toggleFind(false)),
  );

  // --- The editor itself ----------------------------------------------------

  const main = document.createElement('div');
  main.className = 'txed-main';

  const gutter = document.createElement('pre');
  gutter.className = 'txed-code txed-gutter';
  gutter.setAttribute('aria-hidden', 'true');

  const stack = document.createElement('div');
  stack.className = 'txed-stack';

  // One element, not <pre><code>. The browser's own rule for <code> is
  // `font-family: monospace`, which beats the inherited stack and paints the
  // colouring in a different face — a fraction of a pixel narrower per
  // character, so by the fortieth the colours sit three characters left of the
  // caret and deleting takes the wrong letter.
  const hl = document.createElement('pre');
  hl.className = 'txed-code txed-hl';
  hl.setAttribute('aria-hidden', 'true');

  const ta = document.createElement('textarea');
  ta.className = 'txed-code txed-input';
  ta.spellcheck = false;
  ta.autocapitalize = 'off';
  ta.wrap = 'off';
  ta.setAttribute('autocorrect', 'off');

  stack.append(hl, ta);

  /**
   * The same file as a grid, for the ones that are tables.
   *
   * A BOM read down a monospace column with quotes in it is not a bill of
   * materials, it is a wall. Editing a cell writes the row back through the
   * same writer the check covers, so the text and the grid are never two
   * versions of anything — there is one file and two ways of looking at it.
   */
  const grid = document.createElement('div');
  grid.className = 'txed-grid';
  grid.hidden = true;

  main.append(gutter, stack, grid);

  // --- Status ---------------------------------------------------------------

  const status = document.createElement('footer');
  status.className = 'txed-status';
  const where = document.createElement('span');
  const size = document.createElement('span');
  const ending = document.createElement('span');
  const hint = document.createElement('span');
  hint.className = 'txed-hint';
  hint.textContent = 'Esc leaves the editor — the menu keys work again';
  status.append(where, size, ending, hint);

  root.append(bar, findRow, main, status);

  // --- Painting -------------------------------------------------------------

  let painting = 0;
  let lines = -1;

  /** Colour the text and refresh the line numbers. */
  function paint(): void {
    const text = ta.value;
    const tokens = text.length > HIGHLIGHT_LIMIT ? [] : tokenize(text, langId);
    let html = '';
    let at = 0;
    for (const t of tokens) {
      if (t.start < at || t.end > text.length) continue;
      if (t.start > at) html += escape(text.slice(at, t.start));
      html += `<span class="t-${t.type}">${escape(text.slice(t.start, t.end))}</span>`;
      at = t.end;
    }
    html += escape(text.slice(at));
    // A trailing newline is swallowed by <pre>, and the last line would sit one
    // row above the caret without this.
    hl.innerHTML = `${html}\n`;
    numberLines(text);
    sync();
    updateStatus();
  }

  /** The same letters in the same places, uncoloured. Cheap at any size. */
  function paintPlain(): void {
    const text = ta.value;
    hl.textContent = `${text}\n`;
    numberLines(text);
    sync();
    updateStatus();
  }

  function numberLines(text: string): void {
    const n = countLines(text);
    if (n === lines) return;
    lines = n;
    let nums = '';
    for (let i = 1; i <= n; i++) nums += `${i}\n`;
    gutter.textContent = nums;
    gutter.style.width = `${String(n).length + 1}ch`;
  }

  /**
   * Coalesce repaints. Small files recolour on every keystroke; a large one
   * goes plain at once and colours again when the typing stops.
   *
   * The plain pass is what makes the wait bearable: colouring a 200k file takes
   * half a second, and leaving the old markup up meanwhile would show the
   * previous text under the caret rather than what was just typed.
   */
  function repaint(): void {
    const len = ta.value.length;
    if (len < BIG) {
      // Cheap enough to do every tick; one paint per burst of keystrokes.
      if (painting) return;
      painting = window.setTimeout(() => { painting = 0; paint(); }, 0);
      return;
    }
    paintPlain();
    // Put the colouring off until the typing stops, not merely slow it down.
    if (painting) clearTimeout(painting);
    painting = window.setTimeout(
      () => { painting = 0; paint(); }, len < 3 * BIG ? 150 : 400);
  }

  function sync(): void {
    hl.scrollTop = ta.scrollTop;
    hl.scrollLeft = ta.scrollLeft;
    gutter.scrollTop = ta.scrollTop;
  }

  function updateStatus(): void {
    const text = ta.value;
    // In the grid, a line and column of the underlying text mean nothing; the
    // shape of the table is what is worth saying.
    if (gridded) {
      const body = grid.querySelectorAll('tbody tr').length;
      const cols = grid.querySelectorAll('thead th').length - 1;
      where.textContent = `${body} row${body === 1 ? '' : 's'}`;
      size.textContent = `${cols} column${cols === 1 ? '' : 's'}`;
      ending.textContent = eol === '\r\n' ? 'CRLF' : 'LF';
      return;
    }
    const at = ta.selectionStart;
    const before = text.lastIndexOf('\n', at - 1) + 1;
    where.textContent = `Ln ${countLines(text.slice(0, at))}, Col ${at - before + 1}`;
    const sel = ta.selectionEnd - ta.selectionStart;
    size.textContent = sel > 0
      ? `${sel} selected`
      : `${lines} line${lines === 1 ? '' : 's'}, ${text.length} chars`;
    ending.textContent = eol === '\r\n' ? 'CRLF' : 'LF';
  }

  // --- Editing helpers ------------------------------------------------------

  /** Replace the selection, keeping the browser's own undo stack intact. */
  function put(s: string): void {
    ta.focus();
    let ok = false;
    try {
      ok = document.execCommand('insertText', false, s);
    } catch {
      ok = false;
    }
    if (!ok) {
      const a = ta.selectionStart;
      const b = ta.selectionEnd;
      ta.setRangeText(s, a, b, 'end');
    }
    changed();
  }

  function changed(): void {
    repaint();
    if (!loading) opts.onChange?.(getText());
  }

  /** The whole lines the selection touches. */
  function lineSpan(): { from: number; to: number } {
    const text = ta.value;
    const from = text.lastIndexOf('\n', ta.selectionStart - 1) + 1;
    let to = text.indexOf('\n', ta.selectionEnd);
    if (to < 0) to = text.length;
    return { from, to };
  }

  function indentUnit(): string {
    return languageById(langId).indent;
  }

  function indentSelection(out: boolean): void {
    const { from, to } = lineSpan();
    const unit = indentUnit();
    const block = ta.value.slice(from, to);
    const next = block.split('\n').map((l) => {
      if (!out) return l === '' ? l : unit + l;
      if (l.startsWith(unit)) return l.slice(unit.length);
      if (l.startsWith('\t')) return l.slice(1);
      return l.replace(/^ {1,4}/, '');
    }).join('\n');
    if (next === block) return;
    ta.setSelectionRange(from, to);
    put(next);
    ta.setSelectionRange(from, from + next.length);
  }

  /** Enter keeps the indent of the line it left, and opens a level after `{`. */
  function newline(): void {
    const text = ta.value;
    const at = ta.selectionStart;
    const from = text.lastIndexOf('\n', at - 1) + 1;
    const lead = /^[ \t]*/.exec(text.slice(from, at))?.[0] ?? '';
    const last = text.slice(from, at).trimEnd().slice(-1);
    const deeper = last === '{' || last === '(' || last === '[' || last === ':';
    put(`\n${lead}${deeper ? indentUnit() : ''}`);
  }

  /** Ctrl+/ — comment the lines the selection touches, or uncomment them. */
  function toggleComment(): void {
    const lang = languageById(langId);
    const { from, to } = lineSpan();
    const block = ta.value.slice(from, to);
    let next: string;

    if (lang.line) {
      const mark = lang.line;
      const rows = block.split('\n');
      const live = rows.filter((r) => r.trim() !== '');
      const all = live.length > 0 && live.every((r) => r.trimStart().startsWith(mark));
      next = rows.map((r) => {
        if (r.trim() === '') return r;
        if (all) return r.replace(new RegExp(`^(\\s*)${escapeRe(mark)} ?`), '$1');
        const lead = /^\s*/.exec(r)?.[0] ?? '';
        return `${lead}${mark} ${r.slice(lead.length)}`;
      }).join('\n');
    } else if (lang.block) {
      const [open, close] = lang.block;
      const body = block.trim();
      next = body.startsWith(open) && body.endsWith(close)
        ? block.replace(open, '').replace(new RegExp(`${escapeRe(close)}(?![\\s\\S]*${escapeRe(close)})`), '')
        : `${open} ${block} ${close}`;
    } else {
      return;
    }

    ta.setSelectionRange(from, to);
    put(next);
    ta.setSelectionRange(from, from + next.length);
  }

  function gotoLine(): void {
    const asked = window.prompt(`Go to line (1–${lines}):`, '');
    if (asked === null) return;
    const n = Number.parseInt(asked, 10);
    if (!Number.isFinite(n) || n < 1) return;
    const text = ta.value;
    let at = 0;
    for (let i = 1; i < n; i++) {
      const nl = text.indexOf('\n', at);
      if (nl < 0) { at = text.length; break; }
      at = nl + 1;
    }
    ta.focus();
    ta.setSelectionRange(at, at);
    scrollCaretIntoView();
    updateStatus();
  }

  /** Put the caret's line in the middle of the view. */
  function scrollCaretIntoView(): void {
    const before = ta.value.slice(0, ta.selectionStart);
    const row = countLines(before) - 1;
    const h = lineHeight();
    const want = row * h - ta.clientHeight / 2;
    ta.scrollTop = Math.max(0, want);
    sync();
  }

  let cachedLineHeight = 0;
  function lineHeight(): number {
    if (!cachedLineHeight) {
      cachedLineHeight = Number.parseFloat(getComputedStyle(ta).lineHeight) || 20;
    }
    return cachedLineHeight;
  }

  // --- Find and replace -----------------------------------------------------

  let matchCase = false;

  function toggleFind(open: boolean): void {
    findRow.hidden = !open;
    if (!open) { if (!gridded) ta.focus(); return; }
    if (gridded) { findIn.focus(); findIn.select(); countMatches(); return; }
    const sel = ta.value.slice(ta.selectionStart, ta.selectionEnd);
    if (sel && !sel.includes('\n')) findIn.value = sel;
    findIn.focus();
    findIn.select();
    countMatches();
  }

  function needle(): string {
    return findIn.value;
  }

  function haystack(): string {
    return matchCase ? ta.value : ta.value.toLowerCase();
  }

  function term(): string {
    return matchCase ? needle() : needle().toLowerCase();
  }

  function countMatches(): void {
    const t = term();
    if (!t) { count.textContent = ''; return; }
    if (gridded) {
      const n = gridMatches().length;
      count.textContent = n === 0 ? 'none' : `${n} cell${n === 1 ? '' : 's'}`;
      return;
    }
    const hay = haystack();
    let n = 0;
    let i = hay.indexOf(t);
    while (i >= 0) { n++; i = hay.indexOf(t, i + t.length); }
    count.textContent = n === 0 ? 'none' : `${n} found`;
  }

  /** Move to the next match in `dir`, wrapping round the end. */
  function step(dir: 1 | -1): void {
    const t = term();
    if (!t) return;
    if (gridded) { stepCell(dir); return; }
    const hay = haystack();
    let at: number;
    if (dir === 1) {
      at = hay.indexOf(t, ta.selectionStart + 1);
      if (at < 0) at = hay.indexOf(t);
    } else {
      at = hay.lastIndexOf(t, Math.max(0, ta.selectionStart - 1));
      if (at < 0) at = hay.lastIndexOf(t);
    }
    if (at < 0) { count.textContent = 'none'; return; }
    ta.focus();
    ta.setSelectionRange(at, at + t.length);
    scrollCaretIntoView();
    updateStatus();
  }

  function replaceOne(): void {
    const t = term();
    if (!t) return;
    const sel = ta.value.slice(ta.selectionStart, ta.selectionEnd);
    const same = matchCase ? sel === t : sel.toLowerCase() === t;
    if (!same) { step(1); return; }
    put(replIn.value);
    step(1);
  }

  function replaceAll(): void {
    const t = term();
    if (!t) return;
    const text = ta.value;
    const hay = matchCase ? text : text.toLowerCase();
    let out = '';
    let at = 0;
    let n = 0;
    for (;;) {
      const i = hay.indexOf(t, at);
      if (i < 0) break;
      out += text.slice(at, i) + replIn.value;
      at = i + t.length;
      n++;
    }
    if (n === 0) { count.textContent = 'none'; return; }
    out += text.slice(at);
    ta.setSelectionRange(0, text.length);
    put(out);
    ta.setSelectionRange(0, 0);
    count.textContent = `${n} replaced`;
  }

  // --- The grid -------------------------------------------------------------

  let gridded = false;
  /** Rows past this stay in the text view; a table that long is a database. */
  const GRID_ROWS = 2000;

  /** Whether this language opens as a table without being asked. */
  function tabular(): boolean {
    return langId === 'csv';
  }

  /**
   * Whether the grid is worth offering, whatever the language says.
   *
   * The content decides, not the file name. A table saved as `.txt`, a net
   * list that is really delimited, a log of readings — all of them read better
   * in columns, and a guess from the extension is not a reason to withhold the
   * button. Only the *automatic* switch stays tied to CSV, so nothing turns
   * into a spreadsheet unasked.
   */
  function canGrid(): boolean {
    if (langId === 'csv') return true;
    const head = ta.value.slice(0, 8000);
    if (head.trim() === '') return false;
    const rows = parseTable(head).rows.filter((r) => r.length > 0);
    if (rows.length < 2) return false;
    const width = columnCount(rows);
    if (width < 2) return false;
    // Most rows the same width, or this is prose with punctuation in it.
    const even = rows.filter((r) => r.length === width).length;
    if (even < rows.length * 0.7) return false;
    // A last field that is empty on nearly every row means the character found
    // ends the line rather than separating fields — which is what makes every
    // statement of a C or JavaScript file look like a two-column table.
    const dangling = rows.filter((r) => (r[r.length - 1] ?? '') === '').length;
    return dangling < rows.length * 0.7;
  }

  function setGrid(on: boolean): void {
    gridded = on && canGrid();
    gridBtn.classList.toggle('on', gridded);
    gridBtn.textContent = gridded ? 'Text' : 'Columns';
    gridBtn.title = gridded ? 'Show the file as text' : 'Show the file as a table';
    grid.hidden = !gridded;
    stack.hidden = gridded;
    gutter.hidden = gridded || wrapped;
    // Both act on the text, and a caret moving behind a grid nobody can see is
    // worse than not offering them.
    // Wrap is a text idea; Find works on cells instead of hiding.
    wrapBtn.hidden = gridded;
    if (gridded) drawGrid();
    else { paint(); ta.focus(); }
  }

  /** Build the table from the text as it stands. */
  function drawGrid(): void {
    const table = parseTable(ta.value);
    const cols = columnCount(table.rows);
    const shown = Math.min(table.rows.length, GRID_ROWS);

    const el = document.createElement('table');
    el.className = 'txed-table';

    // A column element per column, which is what a dragged width is set on.
    const group = document.createElement('colgroup');
    const colEls: HTMLTableColElement[] = [];
    for (let c = 0; c <= cols; c++) {
      const col = document.createElement('col');
      group.append(col);
      colEls.push(col);
    }
    el.append(group);

    // Column letters, as a spreadsheet has, above the file's own header row.
    const head = document.createElement('thead');
    const letters = document.createElement('tr');
    letters.append(cell('th', '', 'txed-rownum'));
    for (let c = 0; c < cols; c++) {
      const th = cell('th', columnName(c), 'txed-colname');
      letters.append(th);
      addResizer(th, c + 1, el, colEls);
    }
    head.append(letters);
    el.append(head);

    const body = document.createElement('tbody');
    for (let r = 0; r < shown; r++) {
      const row = table.rows[r]!;
      const tr = document.createElement('tr');
      if (r === 0) tr.className = 'txed-header';
      tr.append(cell('th', String(r + 1), 'txed-rownum'));
      for (let c = 0; c < cols; c++) {
        const td = cell('td', row[c] ?? '');
        td.contentEditable = 'true';
        td.spellcheck = false;
        td.dataset.r = String(r);
        td.dataset.c = String(c);
        tr.append(td);
      }
      body.append(tr);
    }
    el.append(body);

    grid.replaceChildren(el);
    // Measured only once it is in the document, then fixed. Widths already
    // dragged are kept, so a redraw does not undo them.
    widths.length = cols + 1;
    sizeColumns(el, colEls);
    if (table.rows.length > shown) {
      const note = document.createElement('p');
      note.className = 'txed-grid-note';
      note.textContent = `Showing the first ${shown} of ${table.rows.length} rows. `
        + 'The text view has all of them.';
      grid.append(note);
    }
    updateStatus();
  }

  // --- Finding in the grid ---------------------------------------------------

  /** Every cell holding the search term, in reading order. */
  function gridMatches(): HTMLTableCellElement[] {
    const t = term();
    if (!t) return [];
    return [...grid.querySelectorAll<HTMLTableCellElement>('tbody td')]
      .filter((td) => {
        const v = td.textContent ?? '';
        return (matchCase ? v : v.toLowerCase()).includes(t);
      });
  }

  /** Move to the next matching cell, wrapping round the end. */
  function stepCell(dir: 1 | -1): void {
    const hits = gridMatches();
    if (hits.length === 0) { count.textContent = 'none'; return; }
    const here = document.activeElement as HTMLTableCellElement | null;
    let at = here ? hits.indexOf(here) : -1;
    at = at < 0 ? (dir === 1 ? 0 : hits.length - 1) : (at + dir + hits.length) % hits.length;
    const td = hits[at]!;
    for (const other of grid.querySelectorAll('.txed-hit')) other.classList.remove('txed-hit');
    td.classList.add('txed-hit');
    td.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    td.focus();
    count.textContent = `${at + 1} of ${hits.length}`;
  }

  /** Replace inside the focused cell, or move to one that matches. */
  function replaceCell(): void {
    const t = term();
    if (!t) return;
    const td = document.activeElement as HTMLTableCellElement | null;
    if (!td || td.tagName !== 'TD' || !grid.contains(td)) { stepCell(1); return; }
    const v = td.textContent ?? '';
    const at = (matchCase ? v : v.toLowerCase()).indexOf(t);
    if (at < 0) { stepCell(1); return; }
    td.textContent = v.slice(0, at) + replIn.value + v.slice(at + t.length);
    commitCell(td);
    stepCell(1);
  }

  /** Replace through every cell at once, then rebuild from the new text. */
  function replaceAllCells(): void {
    const t = term();
    if (!t) return;
    const table = parseTable(ta.value);
    let n = 0;
    for (const row of table.rows) {
      for (let c = 0; c < row.length; c++) {
        const v = row[c]!;
        const hay = matchCase ? v : v.toLowerCase();
        if (!hay.includes(t)) continue;
        let out = '';
        let from = 0;
        for (;;) {
          const i = (matchCase ? v : v.toLowerCase()).indexOf(t, from);
          if (i < 0) break;
          out += v.slice(from, i) + replIn.value;
          from = i + t.length;
          n++;
        }
        row[c] = out + v.slice(from);
      }
    }
    if (n === 0) { count.textContent = 'none'; return; }
    ta.value = writeTable(table);
    changed();
    drawGrid();
    count.textContent = `${n} replaced`;
  }

  // --- Column widths ---------------------------------------------------------

  /** Widths chosen by dragging, in pixels, by column. Cleared on load. */
  let widths: (number | null)[] = [];

  /**
   * Let the browser size the columns, then take those numbers over.
   *
   * Measured after the table is in the document rather than guessed from
   * character counts: the header, the values and the padding all have a say,
   * and the browser has already worked it out. From then on the layout is
   * fixed, which is what makes a dragged width stick instead of being
   * renegotiated by the content on the next redraw.
   */
  function sizeColumns(el: HTMLTableElement, cols: HTMLTableColElement[]): void {
    const head = [...el.querySelectorAll<HTMLTableCellElement>('thead th')];
    const natural = head.map((th) => th.getBoundingClientRect().width);
    el.style.tableLayout = 'fixed';
    let total = 0;
    cols.forEach((col, i) => {
      const px = widths[i] ?? Math.max(36, Math.min(natural[i] ?? 80, 320));
      widths[i] = px;
      col.style.width = `${px}px`;
      total += px;
    });
    el.style.width = `${total}px`;
  }

  /** A grip on the right edge of a heading, dragged to set the width. */
  function addResizer(th: HTMLTableCellElement, index: number,
    el: HTMLTableElement, cols: HTMLTableColElement[]): void {
    const grip = document.createElement('span');
    grip.className = 'txed-resize';
    grip.title = 'Drag to resize this column';
    th.append(grip);

    grip.addEventListener('mousedown', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      const startX = ev.clientX;
      const startW = widths[index] ?? th.getBoundingClientRect().width;
      const move = (m: MouseEvent): void => {
        const px = Math.max(36, startW + (m.clientX - startX));
        widths[index] = px;
        const col = cols[index];
        if (col) col.style.width = `${px}px`;
        el.style.width = `${widths.reduce((a: number, w) => a + (w ?? 0), 0)}px`;
      };
      const up = (): void => {
        document.removeEventListener('mousemove', move);
        document.removeEventListener('mouseup', up);
        root.classList.remove('resizing');
      };
      root.classList.add('resizing');
      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', up);
    });

    // Double-clicking a grip gives that column back to its content.
    grip.addEventListener('dblclick', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      widths[index] = null;
      drawGrid();
    });
  }

  function cell(tag: 'td' | 'th', text: string, cls?: string): HTMLTableCellElement {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    e.textContent = text;
    return e;
  }

  /** A cell was edited: put it back through the writer and into the text. */
  function commitCell(td: HTMLTableCellElement): void {
    const r = Number(td.dataset.r);
    const c = Number(td.dataset.c);
    if (!Number.isFinite(r) || !Number.isFinite(c)) return;
    const table = parseTable(ta.value);
    const row = table.rows[r];
    if (!row) return;
    // A ragged row may be shorter than the grid is wide.
    while (row.length <= c) row.push('');
    const next = td.textContent ?? '';
    if (row[c] === next) return;
    row[c] = next;
    ta.value = writeTable(table);
    changed();
  }

  grid.addEventListener('focusout', (ev) => {
    const td = ev.target as HTMLTableCellElement;
    if (td?.tagName === 'TD') commitCell(td);
  });
  grid.addEventListener('keydown', (ev) => {
    // Ctrl+F from a cell: the textarea's handler cannot see it from here.
    if ((ev.ctrlKey || ev.metaKey) && ev.key.toLowerCase() === 'f') {
      ev.preventDefault();
      toggleFind(true);
      return;
    }
    const td = ev.target as HTMLTableCellElement;
    if (td?.tagName !== 'TD') return;
    // Enter commits rather than opening a line inside the cell — a field with
    // a newline in it has to be quoted, and nobody means that by pressing it.
    if (ev.key === 'Enter' && !ev.shiftKey) {
      ev.preventDefault();
      commitCell(td);
      const below = td.parentElement?.nextElementSibling
        ?.querySelector<HTMLTableCellElement>(`td[data-c="${td.dataset.c}"]`);
      below?.focus();
      return;
    }
    if (ev.key === 'Escape') {
      ev.preventDefault();
      drawGrid();
    }
  });

  // --- Wrap -----------------------------------------------------------------

  let wrapped = false;
  function setWrap(on: boolean): void {
    wrapped = on;
    root.classList.toggle('wrap', on);
    ta.wrap = on ? 'soft' : 'off';
    wrapBtn.classList.toggle('on', on);
    // Numbers cannot follow a wrapped line, so they stand down rather than lie.
    gutter.hidden = on;
    sync();
  }

  // --- Events ---------------------------------------------------------------

  ta.addEventListener('input', () => changed());
  ta.addEventListener('scroll', sync, { passive: true });
  ta.addEventListener('keyup', updateStatus);
  ta.addEventListener('click', updateStatus);
  ta.addEventListener('select', updateStatus);

  ta.addEventListener('keydown', (ev) => {
    const ctrl = ev.ctrlKey || ev.metaKey;

    if (ctrl && !ev.altKey) {
      const k = ev.key.toLowerCase();
      // Select all, cut, copy, paste, undo, redo are the browser's, and stay
      // the browser's. Named rather than left to fall through, so a shortcut
      // added here later cannot quietly take one and replace working native
      // behaviour — clipboard, undo stack and all — with a worse copy.
      if (NATIVE_KEYS.has(k)) return;
      if (k === 's') {
        ev.preventDefault();
        if (ev.shiftKey) opts.onSaveAs?.();
        else opts.onSave?.();
        return;
      }
      if (k === 'f') { ev.preventDefault(); toggleFind(true); return; }
      if (k === 'h') { ev.preventDefault(); toggleFind(true); replIn.focus(); return; }
      if (k === 'g') { ev.preventDefault(); gotoLine(); return; }
      if (k === '/') { ev.preventDefault(); toggleComment(); return; }
      return;
    }

    if (ev.key === 'Tab' && !ctrl) {
      ev.preventDefault();
      const multi = ta.value.slice(ta.selectionStart, ta.selectionEnd).includes('\n');
      if (multi || ev.shiftKey) indentSelection(ev.shiftKey);
      else put(indentUnit());
      return;
    }

    if (ev.key === 'Enter' && !ctrl && !ev.shiftKey) {
      ev.preventDefault();
      newline();
      return;
    }

    if (ev.key === 'Escape' && !findRow.hidden) {
      ev.preventDefault();
      ev.stopPropagation();
      toggleFind(false);
    }
  });

  findIn.addEventListener('input', countMatches);
  for (const box of [findIn, replIn]) {
    box.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        if (box === replIn) replaceOne();
        else step(ev.shiftKey ? -1 : 1);
        return;
      }
      if (ev.key === 'Escape') {
        ev.preventDefault();
        ev.stopPropagation();
        toggleFind(false);
      }
    });
  }

  // --- The handle -----------------------------------------------------------

  function getText(): string {
    return eol === '\r\n' ? ta.value.replace(/\n/g, '\r\n') : ta.value;
  }

  const editor: TextEditor = {
    el: root,
    getText,
    language: () => langId,
    focus: () => ta.focus(),
    refresh: paint,
    find(replace) {
      toggleFind(true);
      if (replace) replIn.focus();
    },
    findNext(back) {
      if (findRow.hidden) { toggleFind(true); return; }
      if (gridded) stepCell(back ? -1 : 1);
      else step(back ? -1 : 1);
    },
    replaceNext() {
      if (findRow.hidden) { toggleFind(true); replIn.focus(); return; }
      if (gridded) replaceCell();
      else replaceOne();
    },
    replaceEvery() {
      if (findRow.hidden) { toggleFind(true); replIn.focus(); return; }
      if (gridded) replaceAllCells();
      else replaceAll();
    },
    load(file) {
      loading = true;
      fileName = file.fileName;
      // Line endings are the file's own: a net list written CRLF must go back
      // out CRLF, whatever the browser prefers.
      eol = /\r\n/.test(file.text) ? '\r\n' : '\n';
      ta.value = file.text.replace(/\r\n?/g, '\n');
      langId = file.lang ?? detectLanguage(file.fileName, ta.value);
      langSel.value = langId;
      title.textContent = fileName;
      note.textContent = file.note ?? '';
      ta.scrollTop = 0;
      ta.scrollLeft = 0;
      ta.setSelectionRange(0, 0);
      lines = -1;
      widths = [];
      if (painting) { clearTimeout(painting); painting = 0; }
      paint();
      // A table opens as a table. That is what it is, and reading a BOM down a
      // column of quoted fields is what the grid exists to stop.
      gridBtn.hidden = !canGrid();
      setGrid(tabular());
      loading = false;
    },
    getViewState: () => ({
      start: ta.selectionStart,
      end: ta.selectionEnd,
      scrollTop: ta.scrollTop,
      scrollLeft: ta.scrollLeft,
    }),
    setViewState(v) {
      const n = ta.value.length;
      ta.setSelectionRange(Math.min(v.start, n), Math.min(v.end, n));
      ta.scrollTop = v.scrollTop;
      ta.scrollLeft = v.scrollLeft;
      sync();
      updateStatus();
    },
  };

  return editor;
}

// ---------------------------------------------------------------------------

function toolButton(label: string, tip: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'txed-btn';
  b.textContent = label;
  b.title = tip;
  b.addEventListener('click', onClick);
  return b;
}

function escape(s: string): string {
  return s.replace(/[&<>]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : '&gt;'));
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function countLines(s: string): number {
  let n = 1;
  for (let i = s.indexOf('\n'); i >= 0; i = s.indexOf('\n', i + 1)) n++;
  return n;
}
